import { type AddToCartHookProps } from "~/features/addToCart/hooks/useAddToCartForm";
import { useMemo } from "react";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { SalesDataProvider } from "~/features/salesCampaign/hooks/useSalesData";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { resolveAddToCartScreen } from "~/features/addToCart/flow/resolveAddToCartScreen";
import type { CloseIntent } from "~/features/addToCart/flow/useAtcFlowController";
import {
  AtcWizardProviders,
  type AddToCartSubmitHandler,
  useAtcWizardProcess,
} from "~/features/addToCart/components/AddToCartProcess/AddToCartWizardShared";
import { AtcWizardScreens } from "~/features/addToCart/components/AddToCartProcess/AtcWizardScreens";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

export interface AddToCartWizardProps extends Omit<AddToCartHookProps, "priceReferenceSelection"> {
  restrictionQuery: string;
  onRequestClose: (intent: CloseIntent) => void;
  onSubmit: AddToCartSubmitHandler;
}

export const AddToCartWizard = ({ restrictionQuery, onRequestClose, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const { selection, status } = usePriceReferenceSelection();

  if (status !== "ready") {
    return null;
  }

  return (
    <ReadyAddToCartWizard
      {...hookProps}
      restrictionQuery={restrictionQuery}
      onRequestClose={onRequestClose}
      onSubmit={onSubmit}
      priceReferenceSelection={selection}
    />
  );
};

const ReadyAddToCartWizard = ({
  restrictionQuery,
  onRequestClose,
  onSubmit,
  priceReferenceSelection,
  ...hookProps
}: AddToCartWizardProps & { priceReferenceSelection: PriceReferenceSelection | undefined }) => {
  const process = useAtcWizardProcess({
    flowKind: "addToCart",
    hookProps: { ...hookProps, priceReferenceSelection },
    onRequestClose,
    onSubmit,
    resolveScreen: resolveAddToCartScreen,
    restrictionQuery,
  });

  const salesData = useMemo(
    () => calculateSalesCampaign4PDP({ productDescriptor: process.productDescriptor }),
    [process.productDescriptor],
  );

  return (
    <SalesDataProvider value={salesData.data}>
      <AtcWizardProviders
        derivedCtx={process.derivedCtx}
        flowContextProps={process.flowContextProps}
        form={process.form}
      >
        <AtcWizardScreens
          canSeeAddToCartDisclaimer={process.canSeeAddToCartDisclaimer}
          canSubmitToCartOrQuote={process.canSubmitToCartOrQuote}
          flowKind="addToCart"
          isSending={process.isSending}
          productDescriptor={process.productDescriptor}
          screen={process.currentScreen}
          submit={process.submit}
        />
      </AtcWizardProviders>
    </SalesDataProvider>
  );
};
